import { createApi } from "@reduxjs/toolkit/query/react";
import { publicBaseQuery } from "@/api/base/public/publicBaseQuery";
import { getHttpStatus, getStatusCodeRaw } from "@/lib/apiErrorBag";

/**
 * Возврат в собственный аккаунт при регистрации по инвайт-ссылке.
 *
 * Человек, который уже участвовал в прошлой олимпиаде, на новой ссылке упирается в
 * «ИИН/телефон уже заняты»: бэк ищет существующий аккаунт по точному совпадению Ф.И.О.,
 * и любое расхождение («Айгүл»/«Айгуль», отчество внутри имени, перепутанные имя с фамилией)
 * закрывает дорогу. Отдельного эндпоинта «присоединиться существующим аккаунтом» нет, поэтому
 * собираем путь из тех, что уже работают: подтверждение личности + смена пароля на тот,
 * который человек ввёл в форме, вход и обновление данных профиля.
 *
 * Бэкенд (routes/api.php):
 *   POST /api/password/request-via-code.json    — код на телефон/почту аккаунта
 *   POST /api/password/change-via-code.json     — смена пароля по этому коду
 *   POST /api/password/check-via-fullname.json  — сверка Ф.И.О. (аккаунты без телефона и почты)
 *   POST /api/password/change-via-fullname.json — смена пароля после такой сверки
 *   GET  /api/profile/user.json                 — кто мы после входа
 *   POST /api/profile/user.json                 — обновление additional (школа, класс, регион)
 */

export type RecoveryVerificationType = "phone" | "email" | "fullname";

/** Код бэка (App\Http\States\Authenticate) приходит строкой. */
const USER_NOT_FOUND = "0103";

/** request-via-code не нашёл аккаунт с таким логином. */
export const isRecoveryUserNotFound = (error: unknown): boolean =>
  String(getStatusCodeRaw(error) ?? "") === USER_NOT_FOUND || getHttpStatus(error) === 404;

export type FullName = { firstname: string; lastname: string };

/** Бэк сверяет Ф.И.О. строго, а фамилию с именем часто вводят наоборот — пробуем оба порядка. */
export const fullnameCandidates = ({ firstname, lastname }: FullName): FullName[] => {
  const typed = { firstname: firstname.trim(), lastname: lastname.trim() };
  return [typed, { firstname: typed.lastname, lastname: typed.firstname }];
};

/**
 * Сверка Ф.И.О. по обоим порядкам из fullnameCandidates.
 * Вернёт порядок, на котором бэк узнал аккаунт (с ним же меняют пароль), или null.
 * 429 пробрасывается: при лимите второй порядок не проверить, а «не совпало» соврало бы.
 */
export const matchFullname = async (
  check: (names: FullName) => Promise<unknown>,
  typed: FullName,
): Promise<FullName | null> => {
  for (const names of fullnameCandidates(typed)) {
    try {
      await check(names);
      return names;
    } catch (e: unknown) {
      if (getHttpStatus(e) === 429) throw e;
    }
  }

  return null;
};

export type RequestRecoveryCodeResponse = {
  status?: string;
  status_code?: string | number;
  verification_type?: RecoveryVerificationType;
};

export type OwnProfileEntity = {
  id?: number;
  group?: string;
  school_id?: number | null;
  firstname?: string | null;
  lastname?: string | null;
  patronymic?: string | null;
  phone?: string | null;
  email?: string | null;
  username?: string | null;
  additional?: Record<string, unknown> | null;
};

type ProfileEnvelope = { entity?: OwnProfileEntity } | OwnProfileEntity;

/** Токен только что выдан входом и ещё не разложен по клиентам — передаём его явно. */
const bearer = (accessToken: string) => ({ Authorization: `Bearer ${accessToken}` });

export const accountRecoveryApi = createApi({
  reducerPath: "accountRecoveryApi",
  baseQuery: publicBaseQuery(),
  endpoints: (builder) => ({
    requestRecoveryCode: builder.mutation<RequestRecoveryCodeResponse, { username: string }>({
      query: ({ username }) => ({
        url: "/api/password/request-via-code.json",
        method: "POST",
        data: { username },
      }),
    }),

    changePasswordViaCode: builder.mutation<unknown, { actionCode: string; password: string }>({
      query: ({ actionCode, password }) => ({
        url: "/api/password/change-via-code.json",
        method: "POST",
        data: {
          action_code: actionCode,
          new_password: password,
          new_password_confirmation: password,
        },
      }),
    }),

    checkAccountByFullname: builder.mutation<
      unknown,
      { username: string; firstname: string; lastname: string }
    >({
      query: (data) => ({
        url: "/api/password/check-via-fullname.json",
        method: "POST",
        data,
      }),
    }),

    changePasswordViaFullname: builder.mutation<
      unknown,
      { username: string; firstname: string; lastname: string; password: string }
    >({
      query: ({ username, firstname, lastname, password }) => ({
        url: "/api/password/change-via-fullname.json",
        method: "POST",
        data: {
          username,
          firstname,
          lastname,
          new_password: password,
          new_password_confirmation: password,
        },
      }),
    }),

    /**
     * additional не входит в набор полей по умолчанию — просим его отдельным заголовком,
     * иначе обновление затрёт данные прошлых олимпиад (бэк заменяет поле целиком).
     */
    fetchOwnProfile: builder.mutation<OwnProfileEntity, { accessToken: string }>({
      query: ({ accessToken }) => ({
        url: "/api/profile/user.json",
        method: "GET",
        headers: { ...bearer(accessToken), "X-Requested-Fields-Extra": "additional" },
      }),
      transformResponse: (response: ProfileEnvelope) =>
        ((response as { entity?: OwnProfileEntity })?.entity ?? response) as OwnProfileEntity,
    }),

    /**
     * Единственный эндпоинт вне регистрации, который активирует инвайт: заводит запись в школе
     * организатора и вызывает InviteCode::activate — usages плюс сценарий кода (задания
     * олимпиады или класс). Ради этого шага весь сценарий и затевался.
     */
    attachInviteSchool: builder.mutation<unknown, { accessToken: string; inviteCode: string }>({
      query: ({ accessToken, inviteCode }) => ({
        url: "/api/profile/attach-school.json",
        method: "POST",
        data: { invite_code: inviteCode },
        headers: bearer(accessToken),
      }),
    }),

    /**
     * Профиль принимает только firstname/lastname/additional — Ф.И.О. отправляем как в аккаунте,
     * меняем ровно то, ради чего человек заполнял форму: школу, класс, регион и район.
     */
    updateOwnProfile: builder.mutation<
      unknown,
      {
        accessToken: string;
        firstname: string;
        lastname: string;
        additional: Record<string, unknown>;
      }
    >({
      query: ({ accessToken, firstname, lastname, additional }) => ({
        url: "/api/profile/user.json",
        method: "POST",
        data: { firstname, lastname, additional },
        headers: bearer(accessToken),
      }),
    }),
  }),
});

export const {
  useRequestRecoveryCodeMutation,
  useChangePasswordViaCodeMutation,
  useCheckAccountByFullnameMutation,
  useChangePasswordViaFullnameMutation,
  useFetchOwnProfileMutation,
  useAttachInviteSchoolMutation,
  useUpdateOwnProfileMutation,
} = accountRecoveryApi;
